import React from 'react';

import {Icon, Text} from '@albert-io/atomic';

import type {QuestionNote} from './annotationNotes';
import AnnotationNotesPanelItem from './AnnotationNotesPanelItem';
import {useQuestionNotes} from './useQuestionNotes';

interface AnnotationNotesPanelListProps {
  questionId: string | undefined;
  /** The note whose editor is open, so its pencil can report the dialog's state. */
  editingNoteId: string | undefined;
  /** Opens a note's editor, hung from the row it belongs to. */
  onEditNote: (note: QuestionNote, row: HTMLLIElement) => void;
}

/** The question's notes in reading order, or an invitation to make one. */
export default function AnnotationNotesPanelList({
  questionId,
  editingNoteId,
  onEditNote
}: AnnotationNotesPanelListProps) {
  const notes = useQuestionNotes(questionId);

  if (notes.length === 0) {
    return (
      <div
        className='gap-x1-half px-x2 py-x5 flex flex-col items-center text-center'
        data-testid='annotation-notes-panel-empty'
      >
        {/* Important beats `.a-icon--size-inherit`'s own font-size, which ties on specificity. */}
        <Icon icon='notes' iconStyle='light' color='tertiary' className='text-[39px]!' />
        <Text size='m' color='tertiary'>
          Highlight items to make notes.
        </Text>
      </div>
    );
  }

  // m-x0: without preflight the UA's 1em list margins survive.
  return (
    <ul className='gap-x1-half m-x0 p-x2 flex list-none flex-col'>
      {notes.map((note) => (
        <AnnotationNotesPanelItem
          key={note.annotation.id}
          note={note}
          isEditing={note.annotation.id === editingNoteId}
          onEdit={onEditNote}
        />
      ))}
    </ul>
  );
}
